import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon, { type IconName } from "../../components/AppIcon";
import UserLayout from "../../components/layouts/UserLayout";
import Button from "../../components/ui/Button";
import Loader from "../../components/ui/Loader";
import { useListAnnouncementsQuery } from "../../store/api/announcementsApi";
import { useGetProfileQuery } from "../../store/api/authApi";
import { useGetMyLeaseQuery } from "../../store/api/leasesApi";
import { useListMaintenanceRequestsQuery } from "../../store/api/maintenanceApi";
import { useListNotificationsQuery } from "../../store/api/notificationsApi";
import { useListPaymentsQuery } from "../../store/api/paymentsApi";
import { useGetPublicPropertyByIdQuery } from "../../store/api/publicPropertiesApi";
import DashboardOverview from "./components/DashboardOverview";
import LeaseDetails from "./components/LeaseDetails";
import MaintenanceSection from "./components/MaintenanceSection";
import NotificationCenter from "./components/NotificationCenter";
import PaymentSection from "./components/PaymentSection";

interface TenantData {
	tenant: {
		id: string | null;
		name: string;
		email: string;
		phone: string;
		profilePicture: string | null;
	};
	lease: {
		id: string;
		lease_code?: string;
		property_address?: string;
		start_date: string;
		end_date: string;
		monthly_rent: number;
		security_deposit?: number;
		status: string;
		renewal_deadline?: string;
	};
	property?: {
		id: string;
		name: string;
		description?: string;
		property_type?: string;
		address: string;
		city?: string;
		state?: string;
		zip_code?: string;
		bedrooms?: number;
		bathrooms?: number;
		square_feet?: number;
		images?: string[];
		features?: string[];
	};
	payments: {
		currentBalance: number;
		nextPaymentDue: string;
		paymentHistory: Array<{
			id: string;
			amount: number;
			date: string;
			status: string;
			method: string;
		}>;
		history: Array<{
			id: string;
			amount: number;
			date: string;
			status: string;
			method: string;
		}>;
	};
	maintenance: {
		activeRequests: number;
		completedRequests: number;
		averageResponseTime: string;
		recentRequests: Array<{
			id: string;
			title: string;
			status: string;
			priority: string;
			date: string;
		}>;
	};
	notifications: Array<{
		id: string;
		type: string;
		title: string;
		message: string;
		date: string;
		read: boolean;
	}>;
}

type SectionType = "dashboard" | "lease" | "payments" | "maintenance";

interface MenuItem {
	id: SectionType;
	label: string;
	icon: IconName;
}

const TenantPortal: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [activeSection, setActiveSection] = useState<SectionType>("dashboard");

	// Read section from URL hash on mount and when location changes
	useEffect(() => {
		const hash = location.hash.replace("#", "");
		if (
			hash &&
			["dashboard", "lease", "payments", "maintenance"].includes(hash)
		) {
			setActiveSection(hash as SectionType);
		}
	}, [location]);

	// Fetch user profile
	const { data: userProfile, isLoading: isLoadingProfile } =
		useGetProfileQuery();

	// Fetch lease
	const { data: leaseData, isLoading: isLoadingLease } = useGetMyLeaseQuery();

	// Fetch property details if lease exists
	const { data: propertyData } = useGetPublicPropertyByIdQuery(
		leaseData?.property_id || "",
		{
			skip: !leaseData?.property_id,
		}
	);

	// Fetch payments
	const { data: paymentsData, isLoading: isLoadingPayments } =
		useListPaymentsQuery({
			page_size: 100,
			ordering: "-due_date",
		});

	// Fetch maintenance requests
	const { data: maintenanceData, isLoading: isLoadingMaintenance } =
		useListMaintenanceRequestsQuery({
			page_size: 100,
		});

	// Fetch notifications
	const { data: notificationsData, isLoading: isLoadingNotifications } =
		useListNotificationsQuery({});

	// Fetch announcements
	const { data: announcementsData, isLoading: isLoadingAnnouncements } =
		useListAnnouncementsQuery({
			page_size: 10,
			ordering: "-created_at",
		});

	// Transform API data to TenantData format
	const tenantData = useMemo<TenantData | null>(() => {
		if (!userProfile) return null;

		// Calculate payment stats
		const payments = paymentsData?.results || [];
		const pendingPayments = payments.filter((p) => p.status === "pending");
		const nextPayment = pendingPayments[0];
		const currentBalance = pendingPayments.reduce(
			(sum, p) => sum + p.amount,
			0,
		);

		// Calculate next payment due date based on lease start date
		const calculateNextPaymentDate = (leaseStartDate: string): string => {
			if (!leaseStartDate) return "";

			const startDate = new Date(leaseStartDate);
			const today = new Date();
			const paymentDay = startDate.getDate(); // Day of month from lease start date

			// Get current year and month
			let nextPaymentDate = new Date(today.getFullYear(), today.getMonth(), paymentDay);

			// If payment date has passed this month, move to next month
			if (nextPaymentDate < today) {
				nextPaymentDate = new Date(today.getFullYear(), today.getMonth() + 1, paymentDay);
			}

			return nextPaymentDate.toISOString();
		};

		const nextPaymentDue = leaseData?.start_date ? calculateNextPaymentDate(leaseData.start_date) : "";

		// Calculate maintenance stats
		const maintenanceRequests = maintenanceData?.results || [];
		const activeRequests = maintenanceRequests.filter(
			(r) => r.status === "pending" || r.status === "in_progress",
		);
		const completedRequests = maintenanceRequests.filter(
			(r) => r.status === "completed",
		);

		// Transform notifications - API returns Notification[] directly
		const notifications =
			notificationsData?.map((n) => ({
				id: n.id,
				type: n.type,
				title: n.title,
				message: n.message,
				date: n.created_at,
				read: n.is_read,
			})) || [];

		// Calculate lease status based on dates
		const calculateLeaseStatus = (endDate: string | undefined): string => {
			if (!endDate) return "active";

			const today = new Date();
			const leaseEnd = new Date(endDate);

			// Reset time to midnight for accurate comparison
			today.setHours(0, 0, 0, 0);
			leaseEnd.setHours(0, 0, 0, 0);

			return leaseEnd < today ? "expired" : "active";
		};

		return {
			tenant: {
				id: userProfile.id,
				name: `${userProfile.first_name} ${userProfile.last_name}`.trim(),
				email: userProfile.email,
				phone: userProfile.phone || "",
				profilePicture: userProfile.avatar || null,
			},
			lease: leaseData
				? {
						id: leaseData.id,
						lease_code: leaseData.lease_code,
						property_address: leaseData.property_address || "",
						start_date: leaseData.start_date,
						end_date: leaseData.end_date,
						monthly_rent: Number(leaseData.monthly_rent),
						security_deposit: leaseData.security_deposit
							? Number(leaseData.security_deposit)
							: 0,
						status: calculateLeaseStatus(leaseData.end_date),
						renewal_deadline: leaseData.renewal_deadline || "",
				  }
				: {
						id: "",
						lease_code: "",
						property_address: "",
						start_date: "",
						end_date: "",
						monthly_rent: 0,
						security_deposit: 0,
						status: "",
						renewal_deadline: "",
				  },
			property: propertyData
				? {
						id: propertyData.id,
						name: propertyData.name,
						description: propertyData.description,
						property_type: propertyData.property_type,
						address: propertyData.address,
						city: propertyData.city,
						state: propertyData.state,
						zip_code: propertyData.zip_code,
						bedrooms: propertyData.bedrooms,
						bathrooms: propertyData.bathrooms,
						square_feet: propertyData.square_feet,
						images: propertyData.images,
						features: propertyData.features,
				  }
				: undefined,
			payments: {
				currentBalance,
				nextPaymentDue: nextPaymentDue,
				paymentHistory: payments.map((p) => ({
					id: p.id,
					amount: p.amount,
					date: p.due_date,
					status: p.status,
					method: p.payment_method || "",
				})),
				history: payments.map((p) => ({
					id: p.id,
					amount: p.amount,
					date: p.paid_date || p.due_date,
					status: p.status,
					method: p.payment_method || "",
				})),
			},
			maintenance: {
				activeRequests: activeRequests.length,
				completedRequests: completedRequests.length,
				averageResponseTime: "0 hours", // TODO: Calculate from completed requests
				recentRequests: maintenanceRequests.slice(0, 5).map((r) => ({
					id: r.id,
					title: r.title,
					status: r.status,
					priority: r.priority,
					date: r.requested_date,
				})),
			},
			notifications,
		};
	}, [
		userProfile,
		leaseData,
		propertyData,
		paymentsData,
		maintenanceData,
		notificationsData,
	]);

	const loading =
		isLoadingProfile ||
		isLoadingLease ||
		isLoadingPayments ||
		isLoadingMaintenance ||
		isLoadingNotifications ||
		isLoadingAnnouncements;

	const navigationItems: (MenuItem & { description: string })[] = [
		{
			id: "dashboard",
			label: "Dashboard",
			icon: "LayoutDashboard",
			description: "Overview",
		},
		{
			id: "lease",
			label: "Lease Details",
			icon: "FileText",
			description: "Agreement & Documents",
		},
		{
			id: "payments",
			label: "Payments",
			icon: "CreditCard",
			description: "Pay Rent & History",
		},
		{
			id: "maintenance",
			label: "Maintenance",
			icon: "Wrench",
			description: "Requests & Status",
		},
	];

	const handleSectionChange = (sectionId: SectionType): void => {
		setActiveSection(sectionId);
		// Update URL hash to allow bookmarking
		window.location.hash = sectionId;
	};

	const handleDownloadLease = () => {
		if (!tenantData?.lease || !tenantData?.tenant) return;

		const lease = tenantData.lease;
		const tenant = tenantData.tenant;

		// Generate lease document
		const leaseDoc = `
LEASE AGREEMENT

Lease ID: ${lease.lease_code || lease.id}
Tenant: ${tenant.name}
Email: ${tenant.email}
Phone: ${tenant.phone || "N/A"}
Property Address: ${lease.property_address || "N/A"}

TERMS:
- Start Date: ${new Date(lease.start_date).toLocaleDateString("en-NG")}
- End Date: ${new Date(lease.end_date).toLocaleDateString("en-NG")}
- Monthly Rent: ₦${lease.monthly_rent.toLocaleString("en-NG")}
- Security Deposit: ₦${(lease.security_deposit || 0).toLocaleString("en-NG")}
- Status: ${lease.status.toUpperCase()}

${
	lease.renewal_deadline
		? `Renewal Deadline: ${new Date(lease.renewal_deadline).toLocaleDateString(
				"en-NG",
		  )}`
		: ""
}

Generated on: ${new Date().toLocaleDateString("en-NG")}
		`.trim();

		// Create a blob and download
		const blob = new Blob([leaseDoc], { type: "text/plain" });
		const url = URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = url;
		link.download = `lease-${lease.lease_code || lease.id}-${
			new Date().toISOString().split("T")[0]
		}.txt`;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		URL.revokeObjectURL(url);
	};

	const handleContactManagement = () => {
		navigate("/messages");
	};

	if (loading) {
		return (
			<UserLayout>
				<Loader
					size="lg"
					showText
					text="Loading tenant portal..."
					className="h-64"
				/>
			</UserLayout>
		);
	}

	const renderActiveSection = () => {
		switch (activeSection) {
			case "dashboard":
				return (
					<DashboardOverview
						tenantData={tenantData}
						onSectionChange={handleSectionChange}
						announcements={announcementsData?.results || []}
					/>
				);
			case "lease":
				return <LeaseDetails lease={leaseData || undefined} />;
			case "payments":
				return <PaymentSection />;
			case "maintenance":
				return <MaintenanceSection />;
			default:
				return (
					<DashboardOverview
						tenantData={tenantData}
						onSectionChange={handleSectionChange}
					/>
				);
		}
	};

	return (
		<UserLayout>
			<div className="w-full mt-4">
				{/* Page Header */}
				<div className="mb-6">
					<div className="flex flex-col lg:flex-row items-start lg:items-center justify-between space-y-4 lg:space-y-0">
						<div>
							<h1 className="text-2xl font-bold text-foreground">
								Welcome, {tenantData?.tenant?.name}
							</h1>
							<p className="text-muted-foreground mt-1">
								{tenantData?.lease?.property_address}
							</p>
						</div>

						<div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
							<Button
								variant="outline"
								iconName="Download"
								iconPosition="left"
								className="w-full sm:w-auto"
								onClick={handleDownloadLease}
								disabled={!tenantData?.lease}
							>
								Download Lease
							</Button>

							<Button
								variant="outline"
								iconName="MessageCircle"
								iconPosition="left"
								className="w-full sm:w-auto"
								onClick={handleContactManagement}
							>
								Contact Management
							</Button>
						</div>
					</div>
				</div>

				<div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
					{/* Navigation Sidebar - 3 cols on desktop */}
					<div className="xl:col-span-3">
						<div className="bg-card border border-border rounded-lg card-shadow overflow-hidden">
							<div className="p-4 border-b border-border">
								<h3 className="font-semibold text-foreground">
									Portal Navigation
								</h3>
							</div>

							<nav className="p-2">
								{navigationItems.map((item) => (
									<button
										key={item.id}
										onClick={() => handleSectionChange(item.id)}
										className={`w-full flex items-center space-x-3 px-3 py-3 rounded-lg text-sm font-medium smooth-transition ${
											activeSection === item.id
												? "bg-primary text-primary-foreground"
												: "text-muted-foreground hover:text-foreground hover:bg-muted"
										}`}
									>
										<Icon
											name={item.icon}
											size={20}
											className={
												activeSection === item.id
													? "text-primary-foreground"
													: ""
											}
										/>
										<div className="flex-1 text-left">
											<div className="font-medium">{item.label}</div>
											<div className="text-xs opacity-75">
												{item.description}
											</div>
										</div>
									</button>
								))}
							</nav>
						</div>

						{/* Notifications Panel */}
						<div className="mt-6">
							<NotificationCenter
								notifications={tenantData?.notifications || []}
							/>
						</div>
					</div>

					{/* Main Content Area - 9 cols on desktop */}
					<div className="xl:col-span-9">
						{renderActiveSection()}
						{/* Pass announcements to DashboardOverview */}
						{activeSection === "dashboard" && announcementsData && (
							<div style={{ display: "none" }}>
								{/* Hidden div to pass announcements data */}
							</div>
						)}
					</div>
				</div>
			</div>
		</UserLayout>
	);
};

export default TenantPortal;
